<script setup>
import { budgetFlexibilityLabel } from '@/utils/inquiryCopy'
import {useReservationsStore, useRestaurantsStore, useUsersStore} from '@/stores'
import { requestedNames, reviewLine } from '@/utils/requestApproval'
import Tooltip from '@/layouts/components/Tooltip.vue'
import AppDateTimePicker from '@core/components/AppDateTimePicker.vue'
import {
  emailValidator,
  requiredValidator,
} from '@validators'
import moment from 'moment-timezone'

import { numberWithCommas } from '@/plugins/misc'
import logoLargeLight from '@/assets/logo.svg'

const emit = defineEmits([
  'update:modelValue',
  'submit',
  'update:isDialogVisible'
])

const usersStore = useUsersStore()

const reservationsStore = useReservationsStore()
let viewingReservation = null;
({  viewingReservation } = storeToRefs(reservationsStore))

const userData = viewingReservation
const isDialogVisible = ref(false)

const form = ref()
const valid = ref(true)

// watch(props, () => {
//   userData.value = structuredClone(toRaw(viewingReservation))
// })
const onFormSubmit = () => {
  console.log("onFormSubmit")
  if(valid.value){
    reservationsStore.confirmViewingReservation()
    emit('update:isDialogVisible', false)
  } else{
    //form is not valid!
  }
}
const onFormReset = () => {
  console.log("onFormReset")
  userData.value = viewingReservation
  emit('update:isDialogVisible', false)
}


const copyIdToClipboard = () => {
  navigator.clipboard.writeText(viewingReservation.value._id)
    .then(() => {
      console.log('Text copied to clipboard')
    })
    .catch(err => {
      console.error('Error in copying text: ', err)
    })
}

//check to see if we should pop up any dialogs when the store changes
//this seems hacky
reservationsStore.$subscribe((mutation, state) => {
  isDialogVisible.value = ( state.viewingReservation != null)
})

const minSpendPerPersonLimit = ref(100)
</script>

<template>
  <VDialog
    v-if="viewingReservation"
    v-model="isDialogVisible"
    :width="$vuetify.display.mdAndDown ? '100%' : 650 "
    persistent
    fullscreen
  >
    <VCard class="pa-sm-4 pa-2">
      <!--      <VCardItem class="text-center"> -->
      <!--        <VCardTitle class="text-h5"> -->
      <!--          Please Review -->
      <!--        </VCardTitle> -->
      <!--        Once submitted, will be shared with {{ viewingReservation.requestedRestaurants}} -->
      <!--      </VCardItem> -->
      <VRow>
        <VCardText>
          <VRow v-if="viewingReservation">
            <img
              class="masthead-content-logo ml-2"
              :src="logoLargeLight"
            >
            <VCol>
              <template v-if="viewingReservation.status =='Accepted'">
                <div
                  class="confirm-title"
                  :title="viewingReservation.id"
                  @click="copyIdToClipboard"
                >
                  Preferred Reservation
                </div>
                <div class="font-weight-bold text-md">
                  {{ viewingReservation.confirmedRestaurant }} ({{ viewingReservation.market }})
                </div>
              </template>
              <div
                v-else
                class="confirm-title"
                :title="viewingReservation.id"
                @click="copyIdToClipboard"
              >
                Referral Request
              </div>
              <div class="text-left text-xs">
                <h3 v-if="viewingReservation.status =='Awaiting Response from Restaurant'">
                  Status: Awaiting Review
                </h3>
                <div v-if="viewingReservation.status =='Accepted' && viewingReservation.reservationType != 'instant' ">
                  Accepted <span v-if="viewingReservation.acceptedAt">{{ moment(viewingReservation.acceptedAt).tz(viewingReservation.timeZone.identifier).format("M/DD/YY [at] h:mma") }} {{ viewingReservation.timeZone.abbreviation }} by {{ viewingReservation.confirmedByName }}</span>
                </div>
                <div v-if="viewingReservation.status =='Accepted' && viewingReservation.reservationType == 'instant' ">
                  Booked <span v-if="viewingReservation.acceptedAt">{{ moment(viewingReservation.acceptedAt).tz(viewingReservation.timeZone.identifier).format("M/DD/YY [at] h:mma") }} {{ viewingReservation.timeZone.abbreviation }}</span>
                </div>
                <div v-if="viewingReservation.submittedAt && viewingReservation.reservationType != 'instant' ">
                  Submitted {{ moment(viewingReservation.submittedAt).tz(viewingReservation.timeZone.identifier).format("M/DD/YY [at] h:mma") }} {{ viewingReservation.timeZone.abbreviation }}
                </div>
                <div v-if="viewingReservation.status =='In Review'">
                  In Review - Submitted {{ moment(viewingReservation.submittedAt).tz(viewingReservation.timeZone.identifier).format("M/DD/YY [at] h:mma") }} {{ viewingReservation.timeZone.abbreviation }}
                </div>
                <div v-if="viewingReservation.status =='Member Action'">
                  Member action required
                </div>
                <!-- RM-359 -->
                <div v-if="viewingReservation.status =='Pending RM Review'">
                  Pending RM Review - {{ viewingReservation.statusNote || reviewLine(requestedNames(viewingReservation)) }}
                </div>
                <!-- The deadline the member was given at submit: RM's review doesn't move it -->
                <div v-if="viewingReservation.status =='Pending RM Review' && viewingReservation?.pendingExpirationTime">
                  Expires {{ moment(viewingReservation.pendingExpirationTime).tz(viewingReservation.timeZone.identifier).format("M/DD/YY [at] h:mma") }} {{ viewingReservation.timeZone.abbreviation }}
                </div>
                <div v-if="(viewingReservation.status =='Awaiting Response from Restaurant' || viewingReservation.status =='Submitted' || viewingReservation.status =='In Review' || viewingReservation.status =='Member Action') && viewingReservation?.requestExpirationTime">
                  Expires {{ moment(viewingReservation.requestExpirationTime).tz(viewingReservation.timeZone.identifier).format("M/DD/YY [at] h:mma") }} {{ viewingReservation.timeZone.abbreviation }}
                </div>
                <div v-if="viewingReservation.status =='Rescinding'">
                  Rescinded by member
                </div>
                <div v-if="viewingReservation.status =='Rescinded'">
                  Rescinded by member
                </div>
                <div v-if="viewingReservation.status =='Canceling'">
                  Canceled by member
                </div>
                <div v-if="viewingReservation.status =='Canceled'">
                  Canceled by member
                </div>
                <div v-if="viewingReservation.status =='Not Available'">
                  Not Available
                </div>
                <div v-if="viewingReservation.status =='No Response'">
                  No Response from Restaurant
                </div>
                <div v-if="viewingReservation.status =='No Response' && viewingReservation?.requestExpirationTime">
                  <br>Expired {{ moment(viewingReservation.requestExpirationTime).tz(viewingReservation.timeZone.identifier).format("M/DD/YY [at] h:mma") }} {{ viewingReservation.timeZone.abbreviation }}
                </div>
                <div v-if="viewingReservation.status =='Declined'">
                  Declined by Restaurant
                </div>
              </div>
            </VCol>
          </VRow>
          <VDivider class="ma-4" />

          <div v-if="viewingReservation">
            <div
              v-if="viewingReservation.status !='Accepted'"
              class="mt-2 font-weight-bold text-md"
            >
              {{ viewingReservation.confirmedRestaurant || viewingReservation.requestedRestaurants }} ({{ viewingReservation.market }})
            </div>

            <div class=" mt-0">
              <!--              <div class="reservation-response-item-detail"> -->
              <!--                <VIcon -->

              <!--                    start -->
              <!--                    size="18" -->
              <!--                    class="reservation-response-item-detail-icon" -->
              <!--                    icon="mdi-map-marker" -->
              <!--                /> -->
              <!--                <span>{{ viewingReservation.market }}</span> -->
              <!--              </div> -->
              <div class="reservation-response-item-detail">
                <VIcon

                  start
                  size="18"
                  class="reservation-response-item-detail-icon"
                  icon="mdi-calendar-blank-outline"
                />
                <span>{{ (viewingReservation.date ? moment(viewingReservation.date).format("dddd, M/D") : "No date selected") }}</span>
              </div>
              <div
                v-if="!viewingReservation.confirmedTime "
                class="reservation-response-item-detail"
              >
                <VIcon
                  start
                  size="18"
                  class="reservation-response-item-detail-icon"
                  icon="mdi-clock-outline"
                />
                <span v-if="viewingReservation.earliestTime ">{{ viewingReservation.preferredTime }}{{ (viewingReservation ? ' '+ viewingReservation.timeZone.abbreviation : '') }}, or {{ viewingReservation.earliestTime.slice(0,-2) }}-{{ viewingReservation.latestTime }}</span>
                <span v-else>{{ viewingReservation.preferredTime }} {{ viewingReservation.timeZone.abbreviation }}  (Preferred)</span>
                <span />
              </div>
              <div
                v-else
                class="reservation-response-item-detail"
              >
                <VIcon
                  start
                  size="18"
                  class="reservation-response-item-detail-icon"
                  icon="mdi-clock-alert-outline"
                />
                <span>{{ moment(viewingReservation.confirmedTime, 'HH:mm').format('h:mma') }} {{ viewingReservation.timeZone.abbreviation }}</span>
              </div>
              <div class="reservation-response-item-detail">
                <VIcon

                  start
                  size="18"
                  class="reservation-response-item-detail-icon"
                  icon="mdi-users-outline"
                />
                <span>{{ viewingReservation.partySize }} {{ parseInt(viewingReservation.partySize) > 1?'people':'person' }}</span>
              </div>
              <div
                v-if="viewingReservation.occasion"
                class="reservation-response-item-detail mt-1"
              >
                <span><b>Occasion:</b> {{ viewingReservation.occasion }}</span>
              </div>
              <div
                v-if="viewingReservation.roomPreference"
                class="reservation-response-item-detail"
              >
                <span><b>Room Preference:</b> {{ viewingReservation.roomPreference }}</span>
              </div>
              <div
                v-if="viewingReservation.seatingPreference"
                class="reservation-response-item-detail"
              >
                <span><b>Seating Preference:</b> {{ viewingReservation.seatingPreference }}</span>
              </div>
              <div
                v-if="viewingReservation.specialRequests"
                class="reservation-response-item-detail"
              >
                <span v-if="viewingReservation?.specialRequestFlexibility?.includes('Yes, ')"><b>Notes / Requests (only want reservation if guaranteed):</b> {{ viewingReservation.specialRequests }} </span>
                <span v-else><b>Notes / Requests:</b> {{ viewingReservation.specialRequests }} </span>
              </div>
              <div
                v-if="viewingReservation.additionalNotes"
                class="reservation-response-item-detail"
              >
                <span><b>Affiliation/notes:</b> {{ viewingReservation.additionalNotes }}</span>
              </div>
            </div>


            <div class="mt-2">
              <template v-if="viewingReservation.actualSpendTotal">
                <div>
                  <b>Total Spend: ${{ numberWithCommas(viewingReservation.actualSpendTotal) }}</b>
                </div>
              </template>

              <template v-else-if="viewingReservation.reservationType == 'general'">
                <div class="mt-2">
                  <b>Guaranteed F&B Spend:</b> ${{ numberWithCommas(viewingReservation.partySize * viewingReservation.minSpendPerPerson) }} (${{ numberWithCommas(viewingReservation.minSpendPerPerson) }}pp)
                </div>
                <div>
                  <b>Gratuity (20%):</b> ${{ numberWithCommas(viewingReservation.partySize * viewingReservation.minSpendPerPerson * 0.2) }}
                </div>
                <div>
                  <b>Tax (12%):</b> ${{ numberWithCommas(Math.round(viewingReservation.partySize * viewingReservation.minSpendPerPerson * 0.12)) }}
                </div>
                <div>
                  <b>Total: ${{ numberWithCommas(Math.round((viewingReservation.partySize * viewingReservation.minSpendPerPerson) + (viewingReservation.partySize * viewingReservation.minSpendPerPerson * 0.2) + (viewingReservation.partySize * viewingReservation.minSpendPerPerson * 0.12))) }}</b>
                </div>
                <div>
                  RM takes no commission/fee
                </div>
              </template>
              <span v-else><b>F&B {{ (viewingReservation.reservationType == 'general' || viewingReservation.reservationType == 'instant') ? 'Min' : 'Budget' }}: ${{ numberWithCommas(viewingReservation.partySize * viewingReservation.minSpendPerPerson) }}</b> (${{ numberWithCommas(viewingReservation.minSpendPerPerson) }}pp) - pre-tax & tip</span>
              
              <div
                v-if="viewingReservation.stripeHoldCapturedAmount"
                class="reservation-item-spend"
              >
                ${{ numberWithCommas(viewingReservation.stripeHoldCapturedAmount.toFixed(2)) }} Charged
              </div>
              <div
                v-else-if="viewingReservation.status == 'Accepted' && viewingReservation.stripeHoldConfirmedAmount === 0"
                class="reservation-item-spend"
              >
                No Hold
              </div>
              <div
                v-else-if="viewingReservation.stripeHoldReleasedAt"
                class="reservation-item-spend"
              >
                Hold Released
              </div>
              <div
                v-else-if="viewingReservation.stripeHoldConfirmedAmount"
                class="reservation-item-spend"
              >
                ${{ numberWithCommas(viewingReservation.stripeHoldConfirmedAmount.toFixed(2)) }} On Hold
              </div>
              <!--              <div v-else-if="viewingReservation.status != 'Accepted' && viewingReservation.stripeHoldMaxAmount" class="reservation-item-spend">💳 hold up to ${{ numberWithCommas(viewingReservation.stripeHoldMaxAmount.toFixed(2)) }}</div> -->
            </div>

            <div
              v-if="viewingReservation.budgetFlexibility"
              class="reservation-response-item-detail, ml-2"
            >
              <span><b>Is this the max budget?:</b> {{ budgetFlexibilityLabel(viewingReservation.budgetFlexibility, viewingReservation.minSpendPerPerson) }}</span>
            </div>
            <div>
              <!--              <div class="mt-2"><b>{{viewingReservation.owner.membershipType }} Member</b></div> -->

              <RequestMemberProfile
                :user="viewingReservation.owner"
                :reservation-type="viewingReservation.reservationType"
              />
            </div>
            <div
              v-if="viewingReservation.hostAffiliated"
              class=" mt-2"
            >
              <b>Affiliation with {{ viewingReservation.requestedRestaurants }}:</b>
              {{ viewingReservation.hostAffiliation }}
            </div>

            <div v-if="viewingReservation?.submittedBy?.email && (viewingReservation?.submittedBy?.email != viewingReservation.owner.email)">
              <div class="mt-2">
                <b>Submitted by</b>
              </div>
              <div
                v-if="viewingReservation.submittedBy.email && viewingReservation.submittedBy.firstName && viewingReservation.submittedBy.lastName"
                class="ml-2"
              >
                <div class="text-sm">
                  {{ viewingReservation.submittedBy.firstName }} {{ viewingReservation.submittedBy.lastName }}
                </div>
                <div class="text-sm">
                  {{ viewingReservation.submittedBy.email }}
                </div>
              </div>
              <div v-else>
                <div class="ml-2">
                  <b><em>Profile Incomplete</em></b>
                </div>
              </div>
            </div>

            <div v-else-if="viewingReservation?.createdBy?.email && (viewingReservation?.createdBy?.email != viewingReservation.owner.email)">
              <div class="mt-2">
                <b>Submitted by</b>
              </div>
              <div
                v-if="viewingReservation.createdBy.email && viewingReservation.createdBy.firstName && viewingReservation.createdBy.lastName"
                class="ml-2"
              >
                <div class="text-sm">
                  {{ viewingReservation.createdBy.firstName }}
                  {{ viewingReservation.createdBy.lastName }}
                </div>
                <div class="text-sm">
                  <a :href="'mailto:'+viewingReservation.createdBy.email">{{ viewingReservation.createdBy.email }}</a>
                </div>
              </div>
              <div v-else>
                <div class="ml-2">
                  <b><em>Profile Incomplete</em></b>
                </div>
              </div>
            </div>
          </div>
        </VCardText>
      </VRow>
    </VCard>
    <VBtn
      icon="$close"
      size="large"
      variant="text"
      color="secondary"
      class="dialog-close-btn"
      @click="reservationsStore.cancelViewReservation()"
    >
      <VIcon>mdi-close</VIcon>
    </VBtn>
  </VDialog>
</template>


<style lang="scss" scoped>
.membership-details-list-item {
  margin-block-end: 0.7rem;
  line-height: 1.2;
}
.dialog-close-btn {
  position: fixed;
  right: 6px;
  top: 6px;
}
.v-label{
  white-space: unset;
}

.min-spend-per-person-select .v-field__field{
  white-space: unset;
  box-sizing: content-box;

}

.v-window{
  overflow: unset;
}

.confirm-title {
  color: var(--ds-text-primary);
  font-family: var(--heading-font);
  font-size: 1.2rem;
  line-height: 1.2em;
}
</style>
